// src/pages/AdminCredentials.js
import React, { useEffect, useState, useCallback } from 'react';
import { apiFetch, formatDateTime, formatDate, timeUntil, isManuallyDeleted, deletedLabel } from '../utils/api';
import TransferProgress from '../components/TransferProgress';
import PasswordResetModal from '../components/PasswordResetModal';

const ACTION_LABELS = {
  transfer_created: 'Created', email_resent: 'Email Resent', email_failed: 'Email Failed', password_reset: 'Password Reset', password_failed: 'Password Failed', otp_requested: 'OTP Sent', otp_verified: 'OTP Verified',
  otp_failed: 'OTP Failed', file_downloaded: 'Viewed', link_expired: 'Expired',
  link_unexpired: 'Unexpired', link_regenerated: 'Regen Link', transfer_deleted: 'Deleted',
};

function StatusBadge({ status }) {
  const map = {
    active: ['status-active', '● Active'],
    'expired-downloaded': ['status-expired-downloaded', '✓ Viewed'],
    'expired-timeout': ['status-expired-timeout', '⏱ Timed out'],
    deleted: ['status-deleted', '🗑 Deleted'],
  };
  const [cls, label] = map[status] || ['status-deleted', status];
  return <span className={`status-badge ${cls}`}>{label}</span>;
}

function CredentialCard({ transfer, onRefresh, showToast }) {
  const [busy, setBusy] = useState('');
  const [newPassword, setNewPassword] = useState(null);
  const isActive = transfer.status === 'active';
  const isExpired = !isActive && transfer.status !== 'deleted';

  const action = async (key, fn, msg) => {
    setBusy(key);
    try { await fn(); await onRefresh(); showToast(msg); }
    catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  const adminResetPassword = async () => {
    if (!window.confirm(`Generate a new password for ${transfer.recipientEmail}? The current password will stop working immediately.`)) return;
    setBusy('reset');
    try {
      const r = await apiFetch(`mgmt/credentials/${transfer.id}/reset-password`, { method: 'POST', body: '{}' });
      setNewPassword(r.password);   // no list refresh here — it could unmount this card and lose the password
    } catch (e) { showToast(`❌ ${e.message}`); }
    finally { setBusy(''); }
  };

  return (
    <div className={`transfer-card${!isActive ? ' expired' : ''}`} style={{ display: 'block', padding: 0 }}>
      <div style={{ padding: '14px 18px 10px', borderBottom: '1px solid var(--g100)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '5px', flexWrap: 'wrap' }}>
          <div className="transfer-title">🔐 {transfer.title}</div>
          <StatusBadge status={transfer.status} />
          {transfer.hasPassword && <span className="status-badge" style={{ background: '#fff8e1', color: '#b45309' }}>🔑 Password</span>}
        </div>
        <div className="transfer-meta">
          <span style={{ fontWeight: 600 }}>{transfer.uploaderName}</span>
          <span style={{ color: 'var(--g500)' }}>{transfer.uploaderEmail}</span>
          <span className="tp-recipient">→ {transfer.recipientEmail}</span>
          <span>📅 {formatDate(transfer.createdAt)}</span>
          {isActive && <span style={{ color: 'var(--green)', fontWeight: 600 }}>⏱ {timeUntil(transfer.expiresAt)}</span>}
          {!isActive && transfer.status !== 'deleted' && transfer.expiredAt && <span>Expired {formatDate(transfer.expiredAt)}</span>}
          {transfer.status === 'deleted' && <span style={{ color: 'var(--red)' }}>🗑 {deletedLabel(transfer, { admin: true })}</span>}
        </div>
      </div>

      <div style={{ padding: '10px 18px', borderBottom: '1px solid var(--g100)' }}>
        <TransferProgress transfer={transfer} kind="credential" horizontal />
      </div>

      <div style={{ padding: '10px 18px', background: 'var(--g50)', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
        {isActive && (
          <>
            {transfer.hasPassword && <button className="btn btn-secondary btn-sm" disabled={!!busy} onClick={adminResetPassword}>{busy === 'reset' ? '…' : '🔑 Reset password'}</button>}
            <button className="btn btn-secondary btn-sm" disabled={!!busy}
              onClick={() => action('extend', () => apiFetch(`mgmt/credentials/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Extended 3 days')}>⏱ Extend 3 days</button>
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => action('expire', () => apiFetch(`mgmt/credentials/${transfer.id}/expire`, { method: 'PUT', body: '{}' }), '✅ Expired')}>✕ Expire now</button>
          </>
        )}
        {isExpired && (
          <>
            {transfer.status !== 'expired-downloaded' && (
              <button className="btn btn-secondary btn-sm" disabled={!!busy}
              onClick={() => action('reactivate', () => apiFetch(`mgmt/credentials/${transfer.id}/extend`, { method: 'PUT', body: '{}' }), '✅ Re-activated')}>🔄 Re-activate (3 days)</button>
            )}
            <button className="btn btn-sm" style={{ background: 'var(--red-light)', color: 'var(--red)', border: '1px solid var(--red)' }} disabled={!!busy}
              onClick={() => { if (!window.confirm('Delete this credential transfer?')) return; action('delete', () => apiFetch(`mgmt/credentials/${transfer.id}`, { method: 'DELETE', body: '{}' }), '✅ Deleted'); }}>🗑 Delete</button>
          </>
        )}
      </div>
      {newPassword && <PasswordResetModal password={newPassword} recipientEmail={transfer.recipientEmail} onClose={() => setNewPassword(null)} />}
    </div>
  );
}

export default function AdminCredentials() {
  const [data, setData] = useState({ active: [], expired: [], total: 0 });
  const [globalAudit, setGlobalAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [auditLoading, setAuditLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState('active');
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setLoadError('');
    try { setData(await apiFetch('mgmt/credentials')); }
    catch (e) { setLoadError(e.message); }
    finally { setLoading(false); }
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try { const d = await apiFetch('mgmt/audit'); setGlobalAudit(d.entries || []); }
    catch (e) { console.error(e); }
    finally { setAuditLoading(false); }
  }, []);

  useEffect(() => { load(); loadAudit(); }, [load, loadAudit]);

  const showToast = msg => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const filter = list => !search ? list : list.filter(t =>
    t.title?.toLowerCase().includes(search.toLowerCase()) ||
    t.uploaderEmail?.toLowerCase().includes(search.toLowerCase()) ||
    t.recipientEmail?.toLowerCase().includes(search.toLowerCase())
  );

  const completedList = (data.expired || []).filter(t => t.status === 'expired-downloaded');   // downloaded / viewed
  const deletedList   = (data.expired || []).filter(isManuallyDeleted);                                       // deleted on purpose
  const expiredList   = (data.expired || []).filter(t => t.status !== 'expired-downloaded' && !isManuallyDeleted(t));   // timed out / auto-purged
  const shown = tab === 'active' ? filter(data.active) : tab === 'completed' ? filter(completedList) : tab === 'expired' ? filter(expiredList) : tab === 'deleted' ? filter(deletedList) : [];

  return (
    <div>
      <div className="page-eyebrow">Administration</div>
      <h1 className="page-title">Manage Credential Transfers</h1>
      <p className="page-subtitle">View and manage all credential transfers across the organisation</p>

      <div className="stat-grid">
        <div className="stat-card stat-card-red"><div className="stat-icon stat-icon-red">🔐</div><div className="stat-info"><div className="stat-value">{data.total}</div><div className="stat-label">Total</div></div></div>
        <div className="stat-card stat-card-green"><div className="stat-icon stat-icon-green">✅</div><div className="stat-info"><div className="stat-value">{data.active?.length || 0}</div><div className="stat-label">Active</div></div></div>
        <div className="stat-card stat-card-blue"><div className="stat-icon stat-icon-blue">👁</div><div className="stat-info"><div className="stat-value">{data.expired?.filter(t => t.status === 'expired-downloaded').length || 0}</div><div className="stat-label">Viewed</div></div></div>
        <div className="stat-card stat-card-amber"><div className="stat-icon stat-icon-amber">⏱</div><div className="stat-info"><div className="stat-value">{data.expired?.filter(t => t.status === 'expired-timeout').length || 0}</div><div className="stat-label">Expired (unviewed)</div></div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="tabs" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab${tab === 'active' ? ' active' : ''}`} onClick={() => setTab('active')}>Active <span className="tab-badge">{data.active?.length || 0}</span></button>
            <button className={`tab${tab === 'completed' ? ' active' : ''}`} onClick={() => setTab('completed')}>Completed <span className="tab-badge">{completedList.length}</span></button>
            <button className={`tab${tab === 'expired' ? ' active' : ''}`} onClick={() => setTab('expired')}>Expired <span className="tab-badge">{expiredList.length}</span></button>
            <button className={`tab${tab === 'deleted' ? ' active' : ''}`} onClick={() => setTab('deleted')}>Deleted <span className="tab-badge">{deletedList.length}</span></button>
            <button className={`tab${tab === 'audit' ? ' active' : ''}`} onClick={() => setTab('audit')}>Audit Log</button>
          </div>
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {tab !== 'audit' && <input className="form-input" style={{ width: '220px' }} placeholder="Search…" value={search} onChange={e => setSearch(e.target.value)} />}
            <button className="btn btn-secondary btn-sm" onClick={() => { load(); loadAudit(); }}>↻</button>
          </div>
        </div>
        <div className="card-body">
          {loadError ? <div className="alert alert-error">❌ {loadError}</div>
          : tab === 'audit' ? (
            auditLoading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
            : globalAudit.length === 0 ? <div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">No audit entries</div></div>
            : globalAudit.map((e, i) => (
              <div key={i} className="audit-entry">
                <span className="audit-time">{formatDateTime(e.timestamp)}</span>
                <span className={`audit-action audit-${e.action}`}>{ACTION_LABELS[e.action] || e.action}</span>
                <span className="audit-detail">{e.uploaderEmail && <span className="audit-email">{e.uploaderEmail}</span>}{e.detail && <span> — {e.detail}</span>}</span>
              </div>
            ))
          ) : loading ? <div style={{ textAlign: 'center', padding: '40px' }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
          : shown.length === 0 ? <div className="empty-state"><div className="empty-state-icon">🗂</div><div className="empty-state-title">No credential transfers found</div></div>
          : (
            <div className="transfer-list">
              {shown.map(t => <CredentialCard key={t.id} transfer={t} onRefresh={load} showToast={showToast} />)}
            </div>
          )}
        </div>
      </div>

      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}
